"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useMobileDebugStore, DebugEvent } from "@/lib/mobile-debug-log";

const formatTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toTimeString().slice(0, 8); // HH:MM:SS
};

const formatData = (data: Record<string, any> | undefined): string => {
  if (!data) return "";
  try {
    return JSON.stringify(data);
  } catch {
    return "[unserializable]";
  }
};

const formatEventForCopy = (event: DebugEvent): string => {
  const time = formatTime(event.timestamp);
  const dataStr = event.data ? `\n  ${formatData(event.data)}` : "";
  return `${time} [${event.category}] ${event.message}${dataStr}`;
};

export const MobileDebugPanel = () => {
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const events = useMobileDebugStore((s) => s.events);
  const clear = useMobileDebugStore((s) => s.clear);

  // Client-side only: check for ?debug=1 and mark as mounted
  useEffect(() => {
    setIsMounted(true);
    const debugParam = new URLSearchParams(window.location.search).get("debug");
    setIsDebugMode(debugParam === "1");
  }, []);

  // SSR guard: render nothing on server
  if (!isMounted) {
    return null;
  }

  // Debug mode guard: render nothing (no portal, no DOM) if not enabled
  if (!isDebugMode) {
    return null;
  }

  const handleCopyAll = async () => {
    const text = events.map(formatEventForCopy).join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  const panelContent = isExpanded ? (
    <div
      data-debug-panel="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        maxHeight: "50vh",
        backgroundColor: "rgba(0, 0, 0, 0.92)",
        color: "#0f0",
        fontSize: 11,
        fontFamily: "monospace",
        zIndex: 999999999,
        display: "flex",
        flexDirection: "column",
        borderBottom: "1px solid #333",
        paddingTop: "calc(env(safe-area-inset-top) + 4px)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 10px",
          borderBottom: "1px solid #333",
          backgroundColor: "rgba(0, 0, 0, 0.95)",
          flexShrink: 0,
        }}
      >
        <span>DEBUG ({events.length})</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleCopyAll}
            style={{
              background: "#333",
              color: "#0f0",
              border: "1px solid #555",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Copy
          </button>
          <button
            onClick={clear}
            style={{
              background: "#333",
              color: "#f80",
              border: "1px solid #555",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Clear
          </button>
          <button
            onClick={() => setIsExpanded(false)}
            style={{
              background: "#333",
              color: "#fff",
              border: "1px solid #555",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Event list */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "6px 10px",
        }}
      >
        {events.length === 0 ? (
          <div style={{ color: "#666" }}>No events yet</div>
        ) : (
          events.map((event) => (
            <div
              key={event.id}
              style={{
                marginBottom: 6,
                borderBottom: "1px solid #222",
                paddingBottom: 4,
              }}
            >
              <div>
                <span style={{ color: "#888" }}>{formatTime(event.timestamp)}</span>{" "}
                <span style={{ color: "#ff0" }}>[{event.category}]</span>{" "}
                <span>{event.message}</span>
              </div>
              {event.data && (
                <div
                  style={{
                    color: "#0aa",
                    fontSize: 10,
                    marginTop: 2,
                    wordBreak: "break-all",
                  }}
                >
                  {formatData(event.data)}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  ) : (
    <div
      data-debug-panel="true"
      onClick={() => setIsExpanded(true)}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 32,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        color: "#0f0",
        fontSize: 12,
        fontFamily: "monospace",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999999999,
        cursor: "pointer",
        borderBottom: "1px solid #333",
        paddingTop: "env(safe-area-inset-top)",
      }}
    >
      DEBUG ({events.length} events) - tap to expand
    </div>
  );

  return createPortal(panelContent, document.body);
};
